import { useEffect, useState, useRef } from 'react'
import { useSnapshot } from 'valtio'
import { options } from '../optionsStorage'

/**
 * Always-visible FPS + percentile-low display. Standard hardcore-gamer trio:
 *   avg = mean FPS over the last ~5 seconds
 *   1% low = 99th-percentile frame-time → reciprocal FPS
 *   0.1% low = 99.9th-percentile frame-time → reciprocal FPS
 *
 * The "lows" surface bad frames that the avg hides. A scene that averages
 * 120 FPS but stalls to 20 every couple seconds will show 1% low ≈ 20.
 */

const BUFFER_SIZE = 600 // ~10s at 60fps; ~5s at 120fps

type Stats = {
  avg: number
  low1: number
  low01: number
}

const computeStats = (buf: Float32Array, count: number): Stats => {
  if (count === 0) return { avg: 0, low1: 0, low01: 0 }
  const used = buf.slice(0, count)
  let sum = 0
  for (const ft of used) sum += ft
  const meanFt = sum / count
  // Sort ascending: lowest frame-time = highest FPS.
  const sorted = Array.from(used).sort((a, b) => a - b)
  // 1% low = worst 1% frame-times = top 1% of sorted ascending.
  const idx1 = Math.max(0, Math.floor(sorted.length * 0.99))
  const idx01 = Math.max(0, Math.floor(sorted.length * 0.999))
  const ft1 = sorted[idx1] ?? meanFt
  const ft01 = sorted[idx01] ?? meanFt
  return {
    avg: 1000 / meanFt,
    low1: 1000 / ft1,
    low01: 1000 / ft01,
  }
}

export default () => {
  const { showFpsCounter } = useSnapshot(options)
  const [stats, setStats] = useState<Stats>({ avg: 0, low1: 0, low01: 0 })
  const bufRef = useRef<Float32Array>(new Float32Array(BUFFER_SIZE))
  const idxRef = useRef(0)
  const countRef = useRef(0)
  const lastTRef = useRef(performance.now())
  const lastUiUpdateRef = useRef(0)

  useEffect(() => {
    if (!showFpsCounter) return
    let raf = 0
    const tick = () => {
      raf = requestAnimationFrame(tick)
      const now = performance.now()
      const ft = now - lastTRef.current
      lastTRef.current = now
      if (ft > 0 && ft < 5000) {
        bufRef.current[idxRef.current] = ft
        idxRef.current = (idxRef.current + 1) % BUFFER_SIZE
        if (countRef.current < BUFFER_SIZE) countRef.current++
      }
      // Update UI ~every 250 ms; reading every frame would re-render too much.
      if (now - lastUiUpdateRef.current > 250) {
        lastUiUpdateRef.current = now
        setStats(computeStats(bufRef.current, countRef.current))
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [showFpsCounter])

  if (!showFpsCounter) return null

  return <div
    style={{
      position: 'fixed',
      top: 2,
      right: 2,
      padding: '2px 6px',
      background: 'rgba(0,0,0,0.5)',
      color: '#fff',
      fontFamily: 'monospace',
      fontSize: 11,
      lineHeight: 1.25,
      zIndex: 20,
      pointerEvents: 'none',
      userSelect: 'none',
      borderRadius: 2,
      minWidth: 90,
      textAlign: 'right',
    }}
  >
    <div>FPS {stats.avg.toFixed(0)}</div>
    <div style={{ opacity: 0.85 }}>1%  {stats.low1.toFixed(0)}</div>
    <div style={{ opacity: 0.65 }}>0.1% {stats.low01.toFixed(0)}</div>
    {/* Tiny shortcut: click to toggle perfProfiler. Saves users from
        typing ?perf=1 in the URL and reloading. Visible only on hover. */}
    <PerfToggle />
  </div>
}

const PerfToggle = () => {
  const [on, setOn] = useState(() => {
    try {
      if (typeof location !== 'undefined' && new URLSearchParams(location.search).get('perf') === '1') return true
      if (typeof localStorage !== 'undefined' && localStorage.perfProfiler === '1') return true
    } catch { /* ssr */ }
    return false
  })
  const flip = () => {
    try {
      localStorage.perfProfiler = on ? '' : '1'
      setOn(!on)
      // The profiler reads the flag at module load; a reload picks up the change.
      // We don't auto-reload here — the user has to refresh themselves.
    } catch { /* silent */ }
  }
  return <div
    onClick={flip}
    title={on ? 'Perf profiler ON — click + reload to disable' : 'Click + reload to enable perf profiler'}
    style={{
      marginTop: 2,
      fontSize: 9,
      opacity: 0.55,
      cursor: 'pointer',
      pointerEvents: 'auto',
      color: on ? '#7fff7f' : '#bbb',
    }}
  >perf {on ? 'on' : 'off'}</div>
}
